interface WatchProviderInfo {
  name: string;
  logoUrl: string;
  type: 'subscription' | 'rent' | 'buy' | 'free' | 'ads';
}

interface WatchProvidersData {
  providers: WatchProviderInfo[];
  tmdbLink: string;
  attribution: string;
}

interface MovieData {
  poster: string;
  director: string;
  synopsis: string;
  cast: string[];
  ratingImdb: string;
  ratingRt: string;
  quote: string;
  genre: string[];
  watchProviders?: WatchProvidersData;
}

interface MovieCardProps {
  title: string;
  year: string;
  movieData: MovieData;
}

export function MovieCard({ title, year, movieData }: MovieCardProps) {
  return (
    <div className="max-w-4xl w-full mx-auto bg-cream text-gray-900 rounded-lg shadow-2xl overflow-hidden animate-slide-up flex flex-col md:flex-row md:min-h-[600px]">
      {/* Left Column: Visuals */}
      <div className="md:w-1/3 bg-black relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-80"
          style={{ backgroundImage: `url(${movieData.poster})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>

        <div className="relative z-10 h-full flex flex-col justify-end p-4 sm:p-6 text-white min-h-[280px] sm:min-h-[300px] md:min-h-0">
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl leading-none mb-2 drop-shadow-md">
            {title}
          </h1>
          <p className="font-serif text-base sm:text-lg italic text-gray-300 mb-4 sm:mb-6 drop-shadow-md">
            {year}
          </p>

          <div className="flex flex-row md:flex-col gap-4 md:gap-4 md:space-y-0 mb-6 sm:mb-8">
            {movieData.ratingImdb && (
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-400 text-black font-bold rounded-full flex items-center justify-center text-xs sm:text-sm shadow-md">
                  IMDb
                </div>
                <span className="font-bold text-lg sm:text-xl drop-shadow-sm">
                  {movieData.ratingImdb}
                </span>
              </div>
            )}
            {movieData.ratingRt && (
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 text-white font-bold rounded-full flex items-center justify-center text-[10px] sm:text-xs p-1 text-center leading-none shadow-md">
                  RT
                </div>
                <span className="font-bold text-lg sm:text-xl drop-shadow-sm">
                  {movieData.ratingRt}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Column: Details */}
      <div className="md:w-2/3 p-5 sm:p-8 md:p-12 flex flex-col relative">
        <div className="relative z-10">
          <div className="mb-5 sm:mb-8 border-b-2 border-primary/20 pb-3 sm:pb-4">
            <h2 className="text-xs font-bold tracking-widest uppercase text-primary mb-1 sm:mb-2">
              Director
            </h2>
            <p className="text-xl sm:text-2xl font-serif text-gray-800">
              {movieData.director}
            </p>
          </div>

          <div className="mb-5 sm:mb-8">
            <h2 className="text-xs font-bold tracking-widest uppercase text-primary mb-2 sm:mb-3">
              Synopsis
            </h2>
            <p className="text-base sm:text-lg leading-relaxed text-gray-700 font-serif">
              {movieData.synopsis}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 mb-5 sm:mb-8">
            <div>
              <h2 className="text-xs font-bold tracking-widest uppercase text-primary mb-2">
                Starring
              </h2>
              <ul className="text-gray-700 text-sm space-y-1">
                {movieData.cast.slice(0, 5).map((actor, i) => (
                  <li key={i}>{actor}</li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-xs font-bold tracking-widest uppercase text-primary mb-2">
                Genre
              </h2>
              <div className="flex flex-wrap gap-2">
                {movieData.genre.map((g, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded uppercase tracking-wide"
                  >
                    {g}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Watch Providers Section */}
          {movieData.watchProviders &&
            movieData.watchProviders.providers.length > 0 && (
              <div className="mb-5 sm:mb-8">
                <h2 className="text-xs font-bold tracking-widest uppercase text-primary mb-2 sm:mb-3">
                  Where to Watch
                </h2>
                <div className="flex flex-wrap gap-3">
                  {movieData.watchProviders.providers.map((provider, i) => (
                    <a
                      key={i}
                      href={movieData.watchProviders?.tmdbLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative"
                      title={`${provider.name} (${provider.type})`}
                    >
                      <img
                        src={provider.logoUrl}
                        alt={provider.name}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg shadow-md hover:scale-110 transition-transform"
                      />
                      <span
                        className={`absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center text-[8px] sm:text-[10px] font-bold shadow ${
                          provider.type === 'subscription'
                            ? 'bg-green-500 text-white'
                            : provider.type === 'free'
                              ? 'bg-blue-500 text-white'
                              : provider.type === 'ads'
                                ? 'bg-yellow-500 text-black'
                                : provider.type === 'rent'
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-purple-500 text-white'
                        }`}
                      >
                        {provider.type === 'subscription'
                          ? '✓'
                          : provider.type === 'free'
                            ? 'F'
                            : provider.type === 'ads'
                              ? 'A'
                              : provider.type === 'rent'
                                ? 'R'
                                : 'B'}
                      </span>
                    </a>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-2">
                  {movieData.watchProviders.attribution}
                </p>
              </div>
            )}

          <div className="mt-auto bg-gray-100 p-4 sm:p-6 rounded-lg border-l-4 border-primary italic text-gray-600 font-serif shadow-inner text-sm sm:text-base">
            "{movieData.quote}"
          </div>
        </div>
      </div>
    </div>
  );
}
